import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { BRANDS, BRAND_LABELS } from "@/data/brands";
import { postAppointment, errorMessage } from "@/lib/api";
import { Eyebrow, MaskLines, Reveal } from "./Motion";

const SLOTS = ["Morning", "Afternoon"];
const EMPTY = { name: "", company: "", email: "", phone: "", preferred_date: "", time_slot: "Morning", message: "" };

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] transition-colors duration-300 ${active ? "border-[#c8a97e] bg-[#c8a97e] text-[#0a0b0d]" : "border-white/20 text-[#d6d3cd] hover:border-white/50"}`}>
    {active && <Check className="h-3.5 w-3.5" />}
    {children}
  </button>
);

const today = () => new Date().toISOString().slice(0, 10);

export const Appointment = ({ preselect }) => {
  const [form, setForm] = useState(EMPTY);
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preselect) setBrands([preselect]);
  }, [preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (b) => setBrands((s) => (s.includes(b) ? s.filter((x) => x !== b) : [...s, b]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postAppointment({ ...form, phone: form.phone || null, message: form.message || null, brands });
      setSent(true);
      setForm(EMPTY);
      setBrands([]);
      toast.success("Appointment request received — a thank-you note is on its way to your inbox.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="appointment" data-testid="appointment-section" className="relative overflow-hidden border-t border-white/10 bg-[#121418]">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 py-24 sm:px-10 sm:py-32 lg:grid-cols-12 lg:px-14">
        <div className="lg:col-span-5">
          <Eyebrow index="05">Appointment</Eyebrow>
          <h2 className="mt-8 font-display text-6xl leading-[0.9] sm:text-7xl">
            <MaskLines inView lines={["Come see", <em key="u" className="text-[#c8a97e]">the collections.</em>]} />
          </h2>
          <Reveal className="mt-12">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad]">Latest collections</p>
            <ul className="mt-5 divide-y divide-white/10 border-y border-white/10">
              {BRANDS.map((b) => (
                <li key={b.slug} className="flex justify-between gap-4 py-3 text-sm">
                  <span className="text-[#f5f3ef]">{b.name === "Guess" ? `Guess ${b.sub}` : b.name}</span>
                  <span className="text-right text-[#b4b2ad]">{b.collections}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-6 lg:col-start-7">
          <form data-testid="appointment-form" onSubmit={submit} className="space-y-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <input required minLength={2} data-testid="appt-name" className="field" placeholder="Your name *" value={form.name} onChange={set("name")} />
              <input required data-testid="appt-company" className="field" placeholder="Store / company *" value={form.company} onChange={set("company")} />
              <input required type="email" data-testid="appt-email" className="field" placeholder="Email *" value={form.email} onChange={set("email")} />
              <input data-testid="appt-phone" className="field" placeholder="Phone" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad]">Brands of interest</p>
              <div className="flex flex-wrap gap-2">
                {BRAND_LABELS.map((b) => <Chip key={b} testid={`appt-brand-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`} active={brands.includes(b)} onClick={() => toggle(b)}>{b}</Chip>)}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <label className="block">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad]">Preferred date *</span>
                <input required type="date" min={today()} data-testid="appt-date" className="field" value={form.preferred_date} onChange={set("preferred_date")} />
              </label>
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad]">Time</span>
                <div className="mt-3 flex gap-2">
                  {SLOTS.map((s) => <Chip key={s} testid={`appt-slot-${s.toLowerCase()}`} active={form.time_slot === s} onClick={() => setForm((f) => ({ ...f, time_slot: s }))}>{s}</Chip>)}
                </div>
              </div>
            </div>
            <textarea data-testid="appt-message" rows={3} className="field resize-none" placeholder="Anything we should prepare?" value={form.message} onChange={set("message")} />
            <div className="flex flex-wrap items-center gap-6">
              <button type="submit" disabled={busy} data-testid="appt-submit" className="group flex items-center gap-3 rounded-full bg-[#c8a97e] px-8 py-4 text-[13px] font-medium uppercase tracking-[0.16em] text-[#0a0b0d] transition-colors duration-300 hover:bg-[#f5f3ef] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />}
                Request appointment
              </button>
              {sent && <span data-testid="appt-success" className="text-sm text-[#c8a97e]">Thank you — your request is in. Check your inbox for our note.</span>}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
